import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { sendAssistantMessage } from '../api/api';
import {
    Box, Fab, Paper, Typography, TextField, IconButton, Stack, Chip,
    Slide, CircularProgress, Avatar, Tooltip, Badge, useTheme, alpha
} from '@mui/material';
import {
    SmartToy as AssistantIcon,
    Close as CloseIcon,
    Send as SendIcon,
    Lightbulb as TipIcon,
    KeyboardArrowDown as MinimizeIcon
} from '@mui/icons-material';

const FloatingAssistant = () => {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [suggestions, setSuggestions] = useState([]);
    const [showBadge, setShowBadge] = useState(true);
    const messagesEndRef = useRef(null);
    const location = useLocation();

    useEffect(() => {
        if (isOpen && messages.length === 0) {
            const welcomeMsg = {
                type: 'assistant',
                text: `Olá! 👋 Sou o assistente do SACH.\n\nEstou aqui para ajudar você a usar o sistema. Pode me perguntar sobre:\n\n• Como fazer upload de arquivos\n• Como configurar regras de rateio\n• Como gerar relatórios\n• O que significa cada métrica\n\nO que gostaria de saber?`,
                timestamp: new Date()
            };
            setMessages([welcomeMsg]);
            fetchSuggestions();
        }
    }, [isOpen]);

    useEffect(() => {
        if (isOpen) {
            fetchSuggestions();
        }
    }, [location.pathname, isOpen]);

    const fetchSuggestions = async () => {
        try {
            const res = await sendAssistantMessage('Olá', location.pathname);
            if (res.data?.suggestions) {
                setSuggestions(res.data.suggestions);
            }
        } catch (e) {
            console.error('Erro ao buscar sugestões:', e);
        }
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (messageText = inputValue) => {
        if (!messageText.trim()) return;

        const userMsg = {
            type: 'user',
            text: messageText,
            timestamp: new Date()
        };
        setMessages(prev => [...prev, userMsg]);
        setInputValue('');
        setIsLoading(true);

        try {
            const res = await sendAssistantMessage(messageText, location.pathname);

            const assistantMsg = {
                type: 'assistant',
                text: res.data?.response || 'Desculpe, não consegui processar sua mensagem.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, assistantMsg]);

            if (res.data?.suggestions) {
                setSuggestions(res.data.suggestions);
            }
        } catch (e) {
            const errorMsg = {
                type: 'assistant',
                text: 'Desculpe, ocorreu um erro. Tente novamente.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMsg]);
        }

        setIsLoading(false);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleOpen = () => {
        setIsOpen(true);
        setShowBadge(false);
    };

    return (
        <>
            {/* Botão flutuante */}
            <Tooltip title="Assistente Virtual" placement="left">
                <Badge
                    badgeContent="?"
                    color="error"
                    invisible={!showBadge}
                    sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: 1300 }}
                >
                    <Fab
                        color="primary"
                        onClick={handleOpen}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a6fd6 0%, #6942a0 100%)',
                            }
                        }}
                    >
                        <AssistantIcon />
                    </Fab>
                </Badge>
            </Tooltip>

            {/* Janela do chat */}
            <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
                <Paper
                    elevation={8}
                    sx={{
                        position: 'fixed',
                        bottom: 24,
                        right: 24,
                        width: 380,
                        maxWidth: 'calc(100vw - 48px)',
                        height: 520,
                        maxHeight: 'calc(100vh - 100px)',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRadius: 3,
                        overflow: 'hidden',
                        zIndex: 1300,
                        bgcolor: 'background.paper'
                    }}
                >
                    {/* Header */}
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            p: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 36, height: 36 }}>
                                <AssistantIcon fontSize="small" />
                            </Avatar>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold">Assistente SACH</Typography>
                                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                    Sempre online para ajudar
                                </Typography>
                            </Box>
                        </Stack>
                        <IconButton size="small" onClick={() => setIsOpen(false)} sx={{ color: 'white' }}>
                            <MinimizeIcon />
                        </IconButton>
                    </Box>

                    {/* Área de mensagens */}
                    <Box
                        sx={{
                            flex: 1,
                            overflowY: 'auto',
                            p: 2,
                            bgcolor: theme.palette.mode === 'dark'
                                ? alpha(theme.palette.background.default, 0.5)
                                : 'grey.100',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5
                        }}
                    >
                        {messages.map((msg, idx) => (
                            <Box
                                key={idx}
                                sx={{
                                    display: 'flex',
                                    justifyContent: msg.type === 'user' ? 'flex-end' : 'flex-start'
                                }}
                            >
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 1.5,
                                        maxWidth: '85%',
                                        bgcolor: msg.type === 'user'
                                            ? 'primary.main'
                                            : 'background.paper',
                                        color: msg.type === 'user' ? 'white' : 'text.primary',
                                        borderRadius: 2,
                                        borderTopRightRadius: msg.type === 'user' ? 0 : 2,
                                        borderTopLeftRadius: msg.type === 'user' ? 2 : 0
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                                    >
                                        {msg.text}
                                    </Typography>
                                </Paper>
                            </Box>
                        ))}

                        {isLoading && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="caption" color="text.secondary">
                                    Pensando...
                                </Typography>
                            </Box>
                        )}

                        <div ref={messagesEndRef} />
                    </Box>

                    {/* Sugestões rápidas */}
                    {suggestions.length > 0 && (
                        <Box sx={{
                            px: 2,
                            py: 1,
                            bgcolor: 'background.paper',
                            borderTop: 1,
                            borderColor: 'divider'
                        }}>
                            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mb: 0.5 }}>
                                <TipIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                                <Typography variant="caption" color="text.secondary">Sugestões:</Typography>
                            </Stack>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {suggestions.slice(0, 2).map((sug, idx) => (
                                    <Chip
                                        key={idx}
                                        label={sug.replace(/^[📁📊💡✅⚠️🧠📈💬❓➕⚙️👋💾]+ ?/, '')}
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleSend(sug)}
                                        sx={{
                                            fontSize: '0.7rem',
                                            height: 24,
                                            cursor: 'pointer',
                                            '&:hover': { bgcolor: 'primary.light', color: 'white' }
                                        }}
                                    />
                                ))}
                            </Box>
                        </Box>
                    )}

                    {/* Input */}
                    <Box sx={{
                        p: 2,
                        bgcolor: 'background.paper',
                        borderTop: 1,
                        borderColor: 'divider'
                    }}>
                        <Stack direction="row" spacing={1}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Digite sua pergunta..."
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyPress={handleKeyPress}
                                disabled={isLoading}
                                multiline
                                maxRows={3}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        bgcolor: theme.palette.mode === 'dark'
                                            ? alpha(theme.palette.background.default, 0.3)
                                            : 'grey.100'
                                    }
                                }}
                            />
                            <IconButton
                                color="primary"
                                onClick={() => handleSend()}
                                disabled={isLoading || !inputValue.trim()}
                                sx={{
                                    bgcolor: 'primary.main',
                                    color: 'white',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    '&.Mui-disabled': { bgcolor: 'grey.300' }
                                }}
                            >
                                <SendIcon fontSize="small" />
                            </IconButton>
                        </Stack>
                    </Box>
                </Paper>
            </Slide>
        </>
    );
};

export default FloatingAssistant;
